<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';

    import { page } from '$app/stores';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import { htmlEmbedFirstInteraction } from '$lib/global/tracking/v4';

    import {
        receiveIframeDimensionsUpdates,
        sendSetBodyContentMessage,
        sendSetHeadContentMessage,
    } from '@perspective-software/cross-origin-html-embed';
    import unescape from 'lodash/unescape';
    import { onDestroy } from 'svelte';

    import { AI_EMBED_LOADING_MIN_HEIGHT, NORMALIZE_CSS } from '../constants';
    import { createEmbedHeightTracker } from '../embedHeightTracker';
    import {
        createUniqueCrossOriginIframeSandboxUrl,
        getEmbedHeightCacheKey,
        readCachedEmbedHeight,
        writeCachedEmbedHeight,
    } from '../helpers';

    // Please also check out "Notes.md" for more details on "first interaction detection".

    export let head = '';
    export let body = '';
    export let enforceCenteredCustomHtmlContent = '';
    export let id = '';
    export let campaignId = '';
    export let firstResultPage = '';
    export let nextPage = '';
    export let provider = '';
    export let layout = DEFAULT_LAYOUT;

    const {
        data: { pageIdSlugMap },
    } = $page;

    const nextPageNavigator = new NextPageNavigator(campaignId, firstResultPage, pageIdSlugMap);

    const iframeUrl = createUniqueCrossOriginIframeSandboxUrl(`${campaignId}-${id}`);

    // FUN-3908: The sandbox iframe mounts with height 0 and only allocates its
    // real height once the cross-origin sandbox has loaded and reported its
    // content dimensions. Start with the height measured on a previous visit
    // (if any), so the layout is correct immediately.
    const heightCacheKey = getEmbedHeightCacheKey(campaignId, id, window.innerWidth);
    const cachedHeight = readCachedEmbedHeight(heightCacheKey);

    let iframeHeight = cachedHeight ?? 0;

    // CVT-4096: Whether the sandbox has finished measuring the embed in this
    // page load, i.e. its reported height has stopped changing.
    let hasMeasurementSettled = false;

    // CVT-4096: Deciding when a reported height is the embed's own and final is
    // subtle enough to live in its own unit-tested module.
    const heightTracker = createEmbedHeightTracker({
        cachedHeight,
        onChange: (state) => {
            iframeHeight = state.height;
            hasMeasurementSettled = state.hasMeasurementSettled;
        },
        onSettle: (height) => writeCachedEmbedHeight(heightCacheKey, height),
    });

    // Until the real height is known, standalone AI embeds reserve most of the
    // viewport so blocks below them (e.g. a CTA button) don't flash near the
    // top of the page and then jump down. Scoped to AI blocks outside layout
    // columns: AI widgets inside a column would break the layout with such a
    // placeholder, and classic custom HTML embeds may be script-only.
    //
    // CVT-4096: A cached height was measured on a previous visit and is correct
    // from the first frame, so those embeds never need the placeholder at all.
    $: reserveLoadingMinHeight =
        provider === 'ai' && !layout.isInColumn && cachedHeight === null && !hasMeasurementSettled;

    let unsubscribeReceiveDimensionsUpdates: (() => void) | null = null;
    let unsubscribeFirstInteractionListener: (() => void) | null = null;
    let unsubscribePageNavigationActionsListener: (() => void) | null = null;
    let unsubscribeContentInjectedListener: (() => void) | null = null;

    onDestroy(() => {
        unsubscribeFirstInteractionListener?.();
        unsubscribePageNavigationActionsListener?.();
        unsubscribeContentInjectedListener?.();
        unsubscribeReceiveDimensionsUpdates?.();

        heightTracker.destroy();
    });

    const headHtmlToInject = `
            ${NORMALIZE_CSS}
            <style>
                body {
                    width: 100%;
                    ${
                        enforceCenteredCustomHtmlContent
                            ? 'display: flex; flex-direction: column; justify-content: center; align-items: center;'
                            : ''
                    }
                }
                style, script, title {
                    display: none !important;
                }
                iframe {
                    max-width: 100% !important;
                }
            </style>
            ${unescape(head)}
    `;

    const bodyHtmlToInject = `
        <script>
            (function() {
                function navigateToNextPage() {
                    window.parent.postMessage({
                        _isSandboxMessage: true,
                        type: "navigate-to-next-page"
                    }, "${window.location.origin}");
                }

                function navigateToResultPage() {
                    window.parent.postMessage({
                        _isSandboxMessage: true,
                        type: "navigate-to-result-page"
                    }, "${window.location.origin}");
                }

                function navigateToPageBySlug(pageSlug) {
                    window.parent.postMessage({
                        _isSandboxMessage: true,
                        type: "navigate-to-page-by-slug",
                        pageSlug: pageSlug
                    }, "${window.location.origin}");
                }

                window.perspective = {
                    navigateToNextPage: navigateToNextPage,
                    navigateToResultPage: navigateToResultPage,
                    navigateToPageBySlug: navigateToPageBySlug,
                };

                // CVT-4096: The host cannot tell a sandbox that has not injected
                // its content yet from one whose content is genuinely only a few
                // px tall (an AI divider settles at ~8px, the empty sandbox
                // document reports ~8-16px), so the sandbox reports its content
                // height explicitly once the content is in the DOM.
                //
                // This script is injected ahead of the embed content, and the
                // whole body is appended in one synchronous task, so a timeout
                // scheduled here runs once the content is present. It must be a
                // timeout rather than a frame callback: these iframes routinely
                // sit far below the fold (not least because of the loading
                // placeholder itself), and browsers do not run frame callbacks
                // for offscreen cross-origin frames.
                //
                // The height is measured and sent here rather than left to the
                // sandbox's ResizeObserver, so the host gets one authoritative
                // value instead of having to guess whether a report describes
                // the content or the empty document that preceded it. Reading
                // the rect forces layout, so the value is never stale.
                setTimeout(function() {
                    window.parent.postMessage({
                        _isSandboxMessage: true,
                        type: "content-injected",
                        contentHeight: document.documentElement.getBoundingClientRect().height
                    }, "${window.location.origin}");
                }, 0);
            })();
        ${'<'}/script>

        <script>
            (function() {
                const initiallyActiveElement = document.activeElement;
                let activeElementCheckInterval = undefined;
                let removeClickListener = null;

                function firstInteractionDetected() {
                    window.parent.postMessage({
                        _isSandboxMessage: true,
                        type: "first-interaction-detected"
                    }, "${window.location.origin}");

                    clearTimeout(activeElementCheckInterval);

                    if ( removeClickListener ) {
                        removeClickListener();
                    }
                }

                activeElementCheckInterval = setInterval(() => {
                    const activeElement = document.activeElement;

                    if ( activeElement !== initiallyActiveElement && activeElement !== document.body && activeElement.tagName === "IFRAME" ) {
                        firstInteractionDetected();
                    }
                }, 333);

                const clickListener = (e) => {
                    if ( e.target !== initiallyActiveElement && e.target !== document.body ) {
                        firstInteractionDetected();
                    }
                };

                document.addEventListener("click", clickListener, true);
                removeClickListener = () => {
                    document.removeEventListener("click", clickListener, true);
                }
            })();
        ${'<'}/script>

        ${unescape(body)}
    `;

    // We need to listen for message sent via postMessage
    // to track a first interaction that has been detected
    // the sandbox iframe.
    const listenForFirstInteraction = (iframe: HTMLIFrameElement) => {
        const messageListener = (event: MessageEvent) => {
            if (event.origin === iframeUrl.origin || event.source === iframe.contentWindow) {
                const message = event.data;

                if (
                    message &&
                    message._isSandboxMessage === true &&
                    message.type === 'first-interaction-detected'
                ) {
                    htmlEmbedFirstInteraction({ embedType: 'sandboxed' });
                }
            }
        };

        window.addEventListener('message', messageListener);

        return () => {
            window.removeEventListener('message', messageListener);
        };
    };

    // We need to listen for message from the iframe to perform
    // page navigation actions triggered from the iframe embed code.
    const listenForPageNavigationActions = (iframe: HTMLIFrameElement) => {
        const messageListener = (event: MessageEvent) => {
            if (event.origin === iframeUrl.origin || event.source === iframe.contentWindow) {
                const message = event.data;

                if (!message || message._isSandboxMessage !== true) {
                    return;
                }

                if (message.type === 'navigate-to-next-page') {
                    nextPageNavigator.go(nextPage);
                } else if (message.type === 'navigate-to-result-page') {
                    nextPageNavigator.go('result');
                } else if (
                    message.type === 'navigate-to-page-by-slug' &&
                    typeof message.pageSlug === 'string'
                ) {
                    nextPageNavigator.go(message.pageSlug);
                }
            }
        };

        window.addEventListener('message', messageListener);

        return () => {
            window.removeEventListener('message', messageListener);
        };
    };

    // CVT-4096: We need to listen for the sandbox reporting that the embed
    // content has been injected into its DOM. Only from that point on does a
    // reported height describe the embed rather than the sandbox shell.
    const listenForContentInjected = (iframe: HTMLIFrameElement) => {
        const messageListener = (event: MessageEvent) => {
            if (event.origin === iframeUrl.origin || event.source === iframe.contentWindow) {
                const message = event.data;

                if (
                    message &&
                    message._isSandboxMessage === true &&
                    message.type === 'content-injected'
                ) {
                    heightTracker.reportInjectedContentHeight(message.contentHeight);
                }
            }
        };

        window.addEventListener('message', messageListener);

        return () => {
            window.removeEventListener('message', messageListener);
        };
    };

    const onIframeLoad = (evt: Event) => {
        const iframe = evt.target as HTMLIFrameElement;

        unsubscribeFirstInteractionListener = listenForFirstInteraction(iframe);
        unsubscribePageNavigationActionsListener = listenForPageNavigationActions(iframe);
        unsubscribeContentInjectedListener = listenForContentInjected(iframe);

        unsubscribeReceiveDimensionsUpdates = receiveIframeDimensionsUpdates(iframe, ({ data }) => {
            heightTracker.reportSandboxHeight(data.documentElementHeight);
        });

        sendSetHeadContentMessage(iframe, headHtmlToInject);
        sendSetBodyContentMessage(iframe, bodyHtmlToInject);
    };

    // Taken from here: https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Permissions-Policy
    const allowPermissions = [
        'accelerometer',
        'ambient-light-sensor',
        'autoplay',
        'battery',
        'bluetooth',
        'browsing-topics',
        'camera',
        'display-capture',
        'document-domain',
        'encrypted-media',
        'execution-while-not-rendered',
        'execution-while-out-of-viewport',
        'fullscreen',
        'gamepad',
        'geolocation',
        'gyroscope',
        'hid',
        'identity-credentials-get',
        'idle-detection',
        'local-fonts',
        'magnetometer',
        'microphone',
        'midi',
        'otp-credentials',
        'payment',
        'picture-in-picture',
        'publickey-credentials-create',
        'publickey-credentials-get',
        'screen-wake-lock',
        'serial',
        'speaker-selection',
        'storage-access',
        'usb',
        'web-share',
        'window-management',
        'xr-spatial-tracking',
    ]
        .map((entry) => entry + ' *')
        .join('; ');
</script>

<div class="fun-flex fun-justify-center">
    <iframe
        src={iframeUrl.href}
        class="fun-w-full"
        title="Custom HTML Embed"
        loading="eager"
        scrolling="no"
        style={`height: ${iframeHeight}px;${
            reserveLoadingMinHeight ? ` min-height: ${AI_EMBED_LOADING_MIN_HEIGHT};` : ''
        }`}
        on:load={onIframeLoad}
        allow={allowPermissions}
        allowfullscreen
    />
</div>
